import React, { useState, useEffect, useRef } from 'react';
import { Type, Search, Loader2 } from 'lucide-react';
import api from '../../utils/api';

// Cache to prevent refetching
let placeholderCache = null;
let cacheLoading = false;

const ContentPlaceholderPicker = ({ onSelect, className = '' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [placeholders, setPlaceholders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const dropdownRef = useRef(null);

    // Fetch placeholders when opened
    useEffect(() => {
        if (!isOpen) return;

        const loadContentPlaceholders = async () => {
            if (placeholderCache) {
                setPlaceholders(placeholderCache);
                return;
            }

            if (cacheLoading) return; // Prevent multiple simultaneous fetches

            setLoading(true);
            cacheLoading = true;
            try {
                const res = await api.get('/placeholders/available');
                if (res.data?.success) {
                    placeholderCache = res.data.content_placeholders || [];
                    setPlaceholders(placeholderCache);
                }
            } catch (err) {
                console.error('Failed to fetch content placeholders:', err);
            } finally {
                setLoading(false);
                cacheLoading = false;
            }
        };

        loadContentPlaceholders();
    }, [isOpen]);

    // Close when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredPlaceholders = placeholders.filter(p =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.slug.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleSelect = (slug) => {
        onSelect(`{${slug}}`);
        setIsOpen(false);
        setSearchQuery('');
    };

    return (
        <div className="relative inline-block" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-violet-600 bg-violet-50 hover:bg-violet-100 border border-violet-200 rounded transition-colors ${className}`}
                title="Insert Employee Data Placeholder"
            >
                <Type className="w-3.5 h-3.5" />
                <span>Insert Data</span>
            </button>

            {isOpen && (
                <div className="absolute z-50 left-0 mt-1 w-64 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden text-left">
                    <div className="p-2 border-b border-gray-100 bg-gray-50">
                        <div className="relative">
                            <Search className="absolute left-2 top-1.5 w-3.5 h-3.5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search placeholders..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-7 pr-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-violet-500 bg-white"
                                autoFocus
                            />
                        </div>
                    </div>

                    <div className="max-h-60 overflow-y-auto p-1 py-1.5">
                        {loading ? (
                            <div className="flex items-center justify-center p-4 text-gray-400">
                                <Loader2 className="w-4 h-4 animate-spin" />
                            </div>
                        ) : filteredPlaceholders.length === 0 ? (
                            <div className="p-3 text-center text-xs text-gray-400">
                                No placeholders found
                            </div>
                        ) : (
                            <div className="space-y-0.5">
                                {filteredPlaceholders.map(p => (
                                    <button
                                        key={p.slug}
                                        onClick={() => handleSelect(p.slug)}
                                        className="w-full text-left px-2 py-1.5 hover:bg-violet-50 rounded group transition-colors"
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="text-xs font-semibold text-gray-700 group-hover:text-violet-700">
                                                {p.name}
                                            </span>
                                            <span className="text-[10px] font-mono text-gray-400 bg-gray-100 px-1 rounded group-hover:bg-violet-100 group-hover:text-violet-600">
                                                {'{' + p.slug + '}'}
                                            </span>
                                        </div>
                                        {p.description && (
                                            <p className="text-[10px] text-gray-500 truncate mt-0.5" title={p.description}>
                                                {p.description}
                                            </p>
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContentPlaceholderPicker;
